'use client'

/**
 * BottomNav - barra de navegação fixa mobile.
 * Identidade Visual eBuz v1.0 (Manual seção 13 - bottom nav pattern).
 *
 * - Sticky bottom, bg-paper/95 + backdrop-blur, border-top ink-7.
 * - Item central destacado em -mt-3 com bg-brand-grad rounded-full + shadow-glow
 *   (se marcado com `primary: true`).
 * - safe-area-inset-bottom respeitado.
 * - Tabs específicas por host:
 *   - ebuz.com.br → 5 tabs (Mapa/Cursos/Livros/Biblioteca/Moedas)
 *   - patyshiatsu.com.br → 4 tabs (Início/Curso/Audiolivro/PDF) apontando direto
 *     pros conteúdos da Paty (não pra listagem genérica /app/cursos).
 *   - outros whitelabels → 4 tabs genéricas (Início/Cursos/Livros/Biblioteca)
 */

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import {
  Home,
  Shield,
  GraduationCap,
  BookOpen,
  FolderArchive,
  FileText,
  Headphones,
  Coins,
  Compass,
  Target,
  type LucideIcon,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useMenuAcessoPago, type MenuAcessoPago } from '@/components/ui/use-menu-acesso-pago'

interface Tab {
  label: string
  href: string
  icon: LucideIcon
  primary?: boolean
  external?: boolean  // se true, usa <a> em vez de <Link> (pra PDF/download)
}

const EBUZ_TABS: Tab[] = [
  { label: 'Livros', href: '/app/livros', icon: BookOpen },
  { label: 'Cursos', href: '/app/cursos', icon: GraduationCap },
  { label: 'Jornada', href: '/app/jornada', icon: Compass, primary: true },
  { label: 'Biblioteca', href: '/app/biblioteca', icon: FolderArchive },
  { label: 'Moedas', href: '/app/creditos', icon: Coins },
]

const PATY_TABS: Tab[] = [
  { label: 'Início', href: '/app', icon: Home },
  { label: 'Curso', href: '/app/curso/metodo-maos', icon: GraduationCap },
  { label: 'Audiolivro', href: '/app/curso/audiolivro-maos', icon: Headphones, primary: true },
  { label: 'Ler', href: '/app/leitor/livro-maos', icon: BookOpen },
]

const GENERIC_WHITELABEL_TABS: Tab[] = [
  { label: 'Início', href: '/app', icon: Home },
  { label: 'Cursos', href: '/app/cursos', icon: GraduationCap },
  { label: 'Livros', href: '/app/livros', icon: BookOpen, primary: true },
  { label: 'Biblioteca', href: '/app/biblioteca', icon: FolderArchive },
]

// Arrasta (Diego) - jornada freemium (SDD 2026-07-23). Menu focado no funil:
// Jornada (central), Biblioteca, Moedas. Antes o dominio caia no fallback
// EBUZ_TABS (Livros/Cursos), que apontavam pra becos sem saida no whitelabel
// (/app/livros -> redirect /app; /app/cursos -> listagem que nao e dele).
const DIEGO_TABS: Tab[] = [
  { label: 'Biblioteca', href: '/app/biblioteca', icon: FolderArchive },
  { label: 'Jornada', href: '/app/jornada', icon: Compass, primary: true },
  { label: 'Moedas', href: '/app/creditos', icon: Coins },
]

const OTHER_WHITELABEL_HOSTS = ['funilchatbot.com.br', 'astrologaevidente.com.br', 'claudiabocchile.com.br']

// Acesso pago (SDD 2026-09-23): no ebuz, Consultoria entra no lugar de Cursos
// (que segue no menu de cima); no Arrasta, Livro e Consultoria entram antes da
// Jornada, que continua no centro. So quando /api/acesso/menu libera.
function comAbasAcessoPago(tabs: Tab[], menu: MenuAcessoPago): Tab[] {
  const consultoria: Tab = { label: 'Consultoria', href: '/app/consultoria', icon: Target }
  if (tabs === EBUZ_TABS) {
    return menu.consultoria ? tabs.map((t) => (t.href === '/app/cursos' ? consultoria : t)) : tabs
  }
  if (tabs === DIEGO_TABS && (menu.consultoria || menu.livro)) {
    const jornada = tabs.find((t) => t.href === '/app/jornada')
    const resto = tabs.filter((t) => t.href !== '/app/jornada')
    const extras: Tab[] = [
      ...(menu.livro ? [{ label: 'Livro', href: '/app/livros', icon: BookOpen }] : []),
      ...(menu.consultoria ? [consultoria] : []),
    ]
    return [...extras, ...(jornada ? [jornada] : []), ...resto]
  }
  return tabs
}

function matchHost(host: string, base: string): boolean {
  return host === base || host.endsWith('.' + base)
}

function resolveTabs(host: string): Tab[] {
  if (matchHost(host, 'patyshiatsu.com.br')) return PATY_TABS
  if (matchHost(host, 'arrastaparacima.com.br')) return DIEGO_TABS
  if (OTHER_WHITELABEL_HOSTS.some(h => matchHost(host, h))) return GENERIC_WHITELABEL_TABS
  return EBUZ_TABS
}

export function BottomNav({ host }: { host?: string }) {
  const pathname = usePathname() ?? ''
  // Estado inicial resolvido com o host que o server passou (elimina o "flash"
  // de EBUZ_TABS no SSR pra whitelabels - antes o menu do Diego piscava
  // Livros/Cursos ate o JS trocar). Sem host, cai no default e o useEffect
  // corrige no cliente (defesa).
  const [tabs, setTabs] = useState<Tab[]>(host ? resolveTabs(host) : EBUZ_TABS)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setTabs(resolveTabs(window.location.hostname))
    }
  }, [])

  // Antes de qualquer return: hook nao pode ficar atras de condicao.
  const menuAcessoPago = useMenuAcessoPago()
  const abas = comAbasAcessoPago(tabs, menuAcessoPago)

  // Ocultar durante execucao de fluxo (foco total)
  if (pathname.includes('/executar')) return null

  // Ocultar em rotas que nao sao /app (inclui /membros que redireciona de /app)
  if (!pathname.startsWith('/app') && !pathname.startsWith('/membros')) return null

  // Ocultar em rotas admin
  if (pathname.startsWith('/admin')) return null

  // Ocultar dentro do player de curso (foco total)
  if (pathname.startsWith('/app/curso/')) return null

  // Ocultar na jornada de contexto (chat imersivo; o menu fixo cobre o campo
  // de mensagem/audio no mobile). Mesmo racional de /executar e /app/curso/.
  if (pathname.startsWith('/app/jornada')) return null

  // Cadastro de primeiro acesso: tela cheia, sem menu (o menu levaria para as
  // areas que a propria tela esta segurando ate o cadastro sair).
  if (pathname.startsWith('/app/perfil/completar')) return null

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-paper/95 backdrop-blur border-t border-ink-7"
      aria-label="Navegação inferior"
    >
      <div className="grid h-16 max-w-screen-sm mx-auto px-2" style={{ gridTemplateColumns: `repeat(${abas.length}, minmax(0, 1fr))` }}>
        {abas.map((tab) => {
          const active = pathname === tab.href || pathname.startsWith(tab.href + '/')
          const Icon = tab.icon

          if (tab.primary) {
            const PrimaryWrapper = tab.external ? 'a' : Link
            const primaryProps = tab.external
              ? { href: tab.href, target: '_blank', rel: 'noopener noreferrer' }
              : { href: tab.href }
            return (
              <PrimaryWrapper
                key={tab.href}
                {...(primaryProps as any)}
                className="flex flex-col items-center justify-center -mt-3 group"
                aria-current={active ? 'page' : undefined}
              >
                <span className={`w-12 h-12 rounded-full bg-brand-grad text-white grid place-items-center shadow-glow transition group-hover:-translate-y-0.5 ${active ? 'ring-2 ring-brand-500 ring-offset-2 ring-offset-paper' : ''}`}>
                  <Icon className="w-5 h-5" strokeWidth={2} />
                </span>
                <span className="text-[10px] font-mono uppercase tracking-wider text-ink-4 mt-0.5">{tab.label}</span>
              </PrimaryWrapper>
            )
          }

          const SecondaryWrapper = tab.external ? 'a' : Link
          const secondaryProps = tab.external
            ? { href: tab.href, target: '_blank', rel: 'noopener noreferrer' }
            : { href: tab.href }
          return (
            <SecondaryWrapper
              key={tab.href}
              {...(secondaryProps as any)}
              className="flex flex-col items-center justify-center gap-0.5 min-w-0"
              aria-current={active ? 'page' : undefined}
            >
              <Icon
                className={`w-5 h-5 transition-colors ${active ? 'text-brand-500' : 'text-ink-4'}`}
                strokeWidth={active ? 2.2 : 1.8}
              />
              <span className={`text-[10px] leading-tight transition-colors ${active ? 'text-ink-0 font-semibold' : 'text-ink-4 font-medium'}`}>
                {tab.label}
              </span>
            </SecondaryWrapper>
          )
        })}
      </div>
      <div style={{ height: 'env(safe-area-inset-bottom)' }} aria-hidden="true" />
    </nav>
  )
}
